import { ClaimTypesEnum } from './../../../../../../../enums/claim-types.enum';
import { Component, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslocoModule } from '@jsverse/transloco';

import { LayoutService } from '../layout-service/layout.service';
import { Claim, ConfigService, OtherPersonContextService } from '../../../../../../../synlab-access-ui-components/core';
import { take } from 'rxjs/operators';
import { MPI_ID_QUERY_PARAM_NAME } from '../../../constants';
import { UserTypes } from './../../../../../../../enums/user-type.enum';
import { hasAnyUserType } from './../../../../../../../utils/user-type-claims';
import { accountEmail } from './../../../../../../../utils/account-email';

/**
 * User types that must not see the "Account preferences" link.
 *
 * This is the single place to change that policy: add a `UserTypes` entry here
 * and the link stops rendering for those users.
 */
export const USER_TYPES_WITHOUT_ACCOUNT_PREFERENCES: readonly UserTypes[] = [
  UserTypes.RESULT_VIEWER,
];

export interface ProfileNavItem {
  label: string;
  path: string;
}

@Component({
    selector: 'app-right-sidebar',
    imports: [TranslocoModule, RouterModule],
    templateUrl: './right-sidebar.component.html',
    styleUrl: './right-sidebar.component.scss'
})
export class RightSidebarComponent implements OnInit, OnDestroy, OnChanges {

  private readonly router = inject(Router);

  public readonly layoutService = inject(LayoutService);

  private readonly configService = inject(ConfigService);

  private readonly otherPersonContext = inject(OtherPersonContextService);

  public readonly showRightSidebar = this.layoutService.showRightSidebar;

  @Input()
  public wards: any[] = [];

  @Output()
  public logOut = new Subject<void>();

  @Input()
  public currentUserClaims: Claim[] = [];

  /**
   * Extra links into the profile sub-sections, shown alongside "Account
   * preferences" so a host app already inside the profile area gives users a
   * second way to reach those sections (SA-21731 - WCAG 2.4.5 Multiple Ways).
   * Empty by default: a host app that doesn't pass any keeps today's single link.
   */
  @Input()
  public profileNavItems: ProfileNavItem[] = [];

  public userEmail: string = '';

  public userHasNoEmail: boolean = false;

  public givenName: string = '';

  public familyName: string = '';

  public userPhone: string = '';

  public baseProfileUrl: string = '';

  private readonly onDestroy$ = new Subject<void>();

  @Input()
  public selectedMpiId: string = '';

  public currentUserMpiId: string = '';

  ngOnInit(): void {
    this.setupConfig();
    this.subscribeToUserInfo();
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.subscribeToUserInfo();
  }

  /**
   * True when the user holds any user type in
   * {@link USER_TYPES_WITHOUT_ACCOUNT_PREFERENCES}.
   */
  public get isAccountPreferencesHidden(): boolean {
    return hasAnyUserType(this.currentUserClaims, USER_TYPES_WITHOUT_ACCOUNT_PREFERENCES);
  }

  private setupConfig() {
    this.configService.config$.pipe(take(1)).subscribe(config => this.baseProfileUrl = config.baseProfileUrl);
  }

  public wardSwitch(mpiId: string) {
    this.showRightSidebar.set(false);
    this.router.navigate(['/results/category'], {
      queryParams: {
        [MPI_ID_QUERY_PARAM_NAME]: mpiId
      }
    });
  }

  /**
   * Switches back to the logged-in user's own data.
   *
   * Leaving the ward has to be said out loud rather than implied by navigating without
   * `mpi-id`: a host app may well be keeping that param attached across navigations
   * (plasma-web does), in which case dropping it from the link achieves nothing.
   * {@link OtherPersonContextService} is the shared channel for that — clearing it is
   * a no-op in an app that doesn't track a viewed person.
   */
  public switchToPrimary() {
    this.showRightSidebar.set(false);
    this.otherPersonContext.clear();
    this.router.navigate(['/results/category']);
  }

  private getClaimValue(claimType: ClaimTypesEnum) {
    return (this.currentUserClaims || []).find(claim => claim.claimType === claimType)?.claimValue || '';
  }

  private subscribeToUserInfo() {
    this.givenName = this.getClaimValue(ClaimTypesEnum.GIVEN_NAME);
    this.familyName = this.getClaimValue(ClaimTypesEnum.FAMILY_NAME);
    // an Italian account carries a system-generated placeholder in `email` and the real
    // address in `contact_email`, so neither claim can be read on its own (SA-21604)
    this.userEmail = accountEmail(this.currentUserClaims);
    this.userHasNoEmail = !this.userEmail;
    this.userPhone = this.getClaimValue(ClaimTypesEnum.PHONE);
    this.currentUserMpiId = this.getClaimValue(ClaimTypesEnum.MPI_ID);
  }

  logout() {
    this.logOut.next();
  }

}
